import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, Grain, EASE } from "@/components/landing/cinematic";

/* ---- Phase artifacts — tiny authentic visuals, not icons ---- */

function ObserveViz() {
    // Warehouse floor plan with a moving observer dot
    return (
        <svg viewBox="0 0 120 76" className="w-full h-20" aria-hidden="true">
            {[8, 36, 64, 92].map((x) => (
                <rect key={x} x={x} y={10} width={18} height={40} rx={2} fill="none" stroke="rgba(247,243,236,0.22)" strokeWidth="1" />
            ))}
            {[8, 36, 64, 92].map((x) =>
                [14, 24, 34].map((y) => (
                    <rect key={`${x}-${y}`} x={x + 3} y={y} width={12} height={6} rx={1} fill="rgba(247,243,236,0.10)" />
                ))
            )}
            <path d="M12 62 H 108" stroke="rgba(139,150,248,0.35)" strokeWidth="1" strokeDasharray="2 4" />
            <circle r="3" fill="#8b96f8">
                <animateMotion dur="6s" repeatCount="indefinite" path="M12 62 H 108" />
            </circle>
        </svg>
    );
}

function IdentifyViz() {
    const rows = [
        { w: "70%", flag: false },
        { w: "84%", flag: true },
        { w: "58%", flag: false },
        { w: "76%", flag: true },
    ];
    return (
        <div className="w-full space-y-2.5 py-2">
            {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${r.flag ? "bg-[#c2410c]" : "bg-[#f7f3ec]/25"}`} />
                    <div
                        className={`h-2 rounded-full ${r.flag ? "bg-[#c2410c]/50" : "bg-[#f7f3ec]/15"}`}
                        style={{ width: r.w }}
                    />
                    {r.flag && (
                        <span className="font-mono text-[8.5px] uppercase tracking-widest text-[#c2410c]">pain</span>
                    )}
                </div>
            ))}
        </div>
    );
}

function BuildViz() {
    const chips = ["Inventory", "Sales", "Finance", "Brain"];
    return (
        <div className="flex w-full flex-wrap items-center justify-center gap-1.5 py-3">
            {chips.map((c, i) => (
                <motion.span
                    key={c}
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 + i * 0.14, ease: EASE }}
                    className={`rounded-md border px-2 py-1 font-mono text-[9.5px] ${
                        c === "Brain"
                            ? "border-[#4353e4]/60 bg-[#4353e4]/15 text-[#8b96f8]"
                            : "border-[#f7f3ec]/15 bg-[#f7f3ec]/[0.06] text-[#f7f3ec]/70"
                    }`}
                >
                    {c}
                </motion.span>
            ))}
        </div>
    );
}

function DeployViz() {
    return (
        <div className="relative mx-auto w-24 py-2">
            <div className="absolute inset-0 rounded-xl bg-[#4353e4]/20 blur-lg" />
            <div className="relative rounded-lg border border-[#f7f3ec]/20 bg-[#1d1a16] p-2">
                <div className="flex gap-1">
                    <span className="h-1 w-1 rounded-full bg-[#f7f3ec]/30" />
                    <span className="h-1 w-1 rounded-full bg-[#f7f3ec]/30" />
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-1">
                    <div className="h-3 rounded-sm bg-emerald-400/30" />
                    <div className="h-3 rounded-sm bg-[#f7f3ec]/10" />
                    <div className="col-span-2 h-5 rounded-sm bg-[#4353e4]/25" />
                </div>
            </div>
        </div>
    );
}

const PHASES = [
    { phase: "Observe", detail: "Live on the shop floor", viz: <ObserveViz /> },
    { phase: "Identify", detail: "Every daily bottleneck", viz: <IdentifyViz /> },
    { phase: "Build", detail: "Module by module", viz: <BuildViz /> },
    { phase: "Deploy", detail: "One intelligent workspace", viz: <DeployViz /> },
];

export default function Story() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
    const draw = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <section
            id="story"
            ref={ref}
            className="relative -mt-8 lg:-mt-12 rounded-t-[32px] lg:rounded-t-[56px] bg-[#151310] pt-20 lg:pt-32 pb-10 text-[#f3efe7] overflow-hidden"
            data-testid="story-section"
        >
            <Grain opacity={0.07} />
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(rgb(247 243 236) 1px, transparent 1px)", backgroundSize: "36px 36px" }}
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <FadeUp>
                        <div className="font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-[#8b96f8]">
                            Built inside a real business
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.08}>
                        <h2 className="mt-4 sm:mt-5 font-display text-[32px] sm:text-5xl lg:text-[54px] font-bold tracking-[-0.03em] text-white leading-[1.05]">
                            Six weeks inside{" "}
                            <span className="font-editorial font-medium text-[#c9c2b4]">Yug Traders LLP.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.16}>
                        <p className="mt-5 text-[14.5px] sm:text-[15px] leading-[1.7] text-[#a29a8e]">
                            Every screen was earned on a real shop floor.
                        </p>
                    </FadeUp>
                </div>

                {/* Timeline — connector draws itself as you scroll */}
                <div className="relative mt-12 lg:mt-20">
                    <svg
                        className="absolute left-0 right-0 top-[26px] hidden lg:block w-full h-[2px] overflow-visible"
                        viewBox="0 0 1200 2"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        <line x1="0" y1="1" x2="1200" y2="1" stroke="rgba(247,243,236,0.12)" strokeWidth="1.5" />
                        <motion.line
                            x1="0" y1="1" x2="1200" y2="1"
                            stroke="url(#story-draw)"
                            strokeWidth="1.5"
                            style={{ pathLength: draw }}
                        />
                        <defs>
                            <linearGradient id="story-draw" x1="0" y1="0" x2="1200" y2="0" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stopColor="#c2410c" />
                                <stop offset="100%" stopColor="#4353e4" />
                            </linearGradient>
                        </defs>
                    </svg>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-8">
                        {PHASES.map((p, i) => (
                            <FadeUp key={p.phase} delay={i * 0.12}>
                                <div className="relative" data-testid={`story-phase-${p.phase.toLowerCase()}`}>
                                    <div className="hidden lg:flex h-[52px] items-center">
                                        <span
                                            className={`flex h-4 w-4 items-center justify-center rounded-full border-2 bg-[#151310] ${
                                                i === PHASES.length - 1 ? "border-[#4353e4]" : "border-[#c2410c]/70"
                                            }`}
                                        >
                                            <span className={`h-1.5 w-1.5 rounded-full ${i === PHASES.length - 1 ? "bg-[#8b96f8]" : "bg-[#c2410c]"}`} />
                                        </span>
                                    </div>
                                    <div className="mt-0 lg:mt-5 rounded-2xl border border-[#f7f3ec]/[0.09] bg-[#f7f3ec]/[0.03] p-5 backdrop-blur-sm transition-colors duration-300 hover:border-[#f7f3ec]/20">
                                        <div className="flex min-h-[88px] items-center justify-center">{p.viz}</div>
                                        <div className="mt-4 border-t border-[#f7f3ec]/[0.08] pt-3.5">
                                            <div className="font-display text-[17px] font-bold tracking-tight text-white">
                                                <span className="mr-2 font-mono text-[10px] font-normal text-[#6b6459]">
                                                    0{i + 1}
                                                </span>
                                                {p.phase}
                                            </div>
                                            <div className="mt-0.5 text-[13px] text-[#a29a8e]">{p.detail}</div>
                                        </div>
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                {/* Thread down into the Brain centerpiece */}
                <div className="mt-20 flex flex-col items-center">
                    <FadeUp>
                        <p className="font-mono text-[11.5px] uppercase tracking-[0.24em] text-[#8b96f8]">
                            Everything feeds one mind
                        </p>
                    </FadeUp>
                    <motion.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: EASE }}
                        className="mt-5 h-14 w-px origin-top bg-gradient-to-b from-[#8b96f8]/70 to-transparent"
                    />
                </div>
            </div>
        </section>
    );
}
